import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { X, SlidersHorizontal } from "lucide-react";

export default function FilterModal({ onClose, onApplied }) {
  const { user, setUser } = useAuth();
  const [gender, setGender] = useState(user?.filter_gender || "everyone");
  const [ageMin, setAgeMin] = useState(user?.filter_age_min ?? 18);
  const [ageMax, setAgeMax] = useState(user?.filter_age_max ?? 60);
  const [distance, setDistance] = useState(user?.filter_distance_km ?? 15);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const min = Math.max(18, Math.min(Number(ageMin) || 18, Number(ageMax) || 60));
    const max = Math.min(99, Math.max(Number(ageMax) || 60, min));
    setSaving(true);
    try {
      const { data } = await api.put("/profile/filters", {
        filter_gender: gender, filter_age_min: min, filter_age_max: max,
        filter_distance_km: Number(distance),
      });
      setUser(data);
      toast.success("Filters updated");
      onApplied();
    } catch {
      toast.error("Could not save filters");
    } finally {
      setSaving(false);
    }
  };

  const genders = [{ v: "female", l: "Women" }, { v: "male", l: "Men" }, { v: "everyone", l: "Everyone" }];

  return (
    <div className="absolute inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="filter-modal" className="relative bg-[#15121E] border-t border-white/10 rounded-t-3xl p-6 pb-8 animate-float-up">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-rose-500" />
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Discovery Filters</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 grid place-items-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>

        <label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Show me</label>
        <div className="flex gap-2 mt-2 mb-6">
          {genders.map((g) => (
            <button key={g.v} data-testid={`filter-gender-${g.v}`} onClick={() => setGender(g.v)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${gender === g.v ? "bg-gradient-to-r from-rose-500 to-rose-600 text-white" : "bg-white/5 border border-white/10 text-slate-300"}`}>
              {g.l}
            </button>
          ))}
        </div>

        <label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Age range</label>
        <div className="flex items-center gap-3 mt-2 mb-7">
          <AgeInput testid="filter-age-min-input" label="From" value={ageMin} onChange={setAgeMin} />
          <span className="text-slate-500 mt-5">—</span>
          <AgeInput testid="filter-age-max-input" label="To" value={ageMax} onChange={setAgeMax} />
        </div>

        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Max distance</label>
          <span className="text-sm font-semibold text-rose-400" data-testid="filter-distance-value">within {distance} km</span>
        </div>
        <input
          data-testid="filter-distance-input"
          type="range" min={1} max={30} value={distance}
          onChange={(e) => setDistance(e.target.value)}
          className="w-full mb-7 accent-rose-500 cursor-pointer"
        />

        <button data-testid="filter-apply-button" onClick={save} disabled={saving}
          className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#FF2E63] to-[#FF6B8B] text-white font-bold active:scale-[0.98] transition-transform disabled:opacity-60">
          {saving ? "Saving…" : "Apply Filters"}
        </button>
      </div>
    </div>
  );
}

function AgeInput({ testid, label, value, onChange }) {
  return (
    <div className="flex-1">
      <span className="text-[11px] text-slate-500">{label}</span>
      <input data-testid={testid} type="number" min={18} max={99} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-white text-center font-semibold focus:outline-none focus:border-rose-500/60" />
    </div>
  );
}
